import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Timer, Loader2, ChevronLeft, ChevronRight, Flag, CheckCircle2, XCircle,
  Trophy, AlertTriangle, RotateCcw, ClipboardList,
} from "lucide-react";

const EXAM_LABEL = {
  cphq: { name: "CPHQ", color: "bg-violet-500/15 text-violet-300 border-violet-500/40", mins: 32 },
  cphrm: { name: "CPHRM", color: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40", mins: 27 },
  chc: { name: "CHC", color: "bg-amber-500/15 text-amber-300 border-amber-500/40", mins: 25 },
  mixed: { name: "Mixed", color: "bg-rose-500/15 text-rose-300 border-rose-500/40", mins: 30 },
};
const LS_KEY = "niko_certprep_active_mock";
const fmt = (s) => `${String(Math.floor(Math.max(0, s) / 60)).padStart(2, "0")}:${String(Math.max(0, s) % 60).padStart(2, "0")}`;

export function MockExam() {
  const [mock, setMock] = useState(null);
  const [starting, setStarting] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/certprep/mock/history`, { withCredentials: true });
      setHistory(data);
    } catch { /* non-fatal */ }
  };

  useEffect(() => {
    loadHistory();
    const saved = localStorage.getItem(LS_KEY);
    if (!saved) return;
    axios.get(`${API}/certprep/mock/${saved}`, { withCredentials: true })
      .then(({ data }) => (["in_progress", "building"].includes(data.status) ? setMock(data) : localStorage.removeItem(LS_KEY)))
      .catch(() => localStorage.removeItem(LS_KEY));
  }, []);

  // Poll while the background builder writes the 25 questions
  useEffect(() => {
    if (mock?.status !== "building") return;
    const t = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/certprep/mock/${mock.mock_id}`, { withCredentials: true });
        if (data.status === "failed") {
          localStorage.removeItem(LS_KEY);
          toast.error(data.error || "Couldn't build the mock — try again");
          setMock(null);
        } else if (data.status !== "building") {
          setMock(data);
        }
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearInterval(t);
  }, [mock?.status, mock?.mock_id]);

  const start = async (exam) => {
    setStarting(true);
    try {
      const { data } = await axios.post(`${API}/certprep/mock/start`, { exam }, { withCredentials: true });
      localStorage.setItem(LS_KEY, data.mock_id);
      setMock(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start the mock — try again");
    } finally { setStarting(false); }
  };

  const onSubmitted = (result) => {
    localStorage.removeItem(LS_KEY);
    setMock(result);
    loadHistory();
  };

  const reset = () => { localStorage.removeItem(LS_KEY); setMock(null); };

  if (starting || mock?.status === "building") return <BuildingScreen />;
  if (mock?.status === "in_progress") return <MockRunner mock={mock} onSubmitted={onSubmitted} />;
  if (mock?.status === "submitted") return <MockResults mock={mock} onDone={reset} />;
  return <MockSetup onStart={start} history={history} onReview={(m) => axios.get(`${API}/certprep/mock/${m.mock_id}`, { withCredentials: true }).then(r => setMock(r.data))} />;
}

/* ------------------------------- Setup ------------------------------- */
function MockSetup({ onStart, history, onReview }) {
  return (
    <div className="space-y-4" data-testid="mock-setup">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base md:text-lg flex items-center gap-2">
            <Timer className="w-4 h-4 text-primary" /> 25-question timed mock
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Real-exam pacing · auto-submits at zero · 70% to pass. No rationale until you finish — just like test day.
          </p>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(EXAM_LABEL).map(([key, m]) => (
            <button
              key={key}
              onClick={() => onStart(key)}
              data-testid={`mock-start-${key}`}
              className="text-left rounded-lg border border-zinc-700 p-4 hover:border-primary hover:bg-primary/5 transition-colors"
            >
              <Badge className={m.color}>{m.name}</Badge>
              <div className="mt-2 text-sm font-semibold">{m.mins} minutes</div>
              <div className="text-[11px] text-muted-foreground">
                {key === "mixed" ? "9 CPHQ · 8 CPHRM · 8 CHC" : `25 ${m.name} questions`}
              </div>
            </button>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><ClipboardList className="w-4 h-4" /> Past mocks</CardTitle></CardHeader>
        <CardContent>
          {history.length === 0 && <div className="text-sm text-muted-foreground" data-testid="mock-history-empty">No mocks yet. Your first readout lands here.</div>}
          {history.map((m) => (
            <button
              key={m.mock_id}
              onClick={() => m.status === "submitted" && onReview(m)}
              disabled={m.status !== "submitted"}
              data-testid={`mock-history-${m.mock_id}`}
              className="w-full flex items-center gap-3 py-2 border-b border-zinc-800 last:border-0 text-left hover:bg-primary/5 rounded px-1 disabled:opacity-60 disabled:hover:bg-transparent"
            >
              <Badge className={`text-[10px] ${EXAM_LABEL[m.exam]?.color}`}>{EXAM_LABEL[m.exam]?.name}</Badge>
              <div className="text-xs text-muted-foreground flex-1">{new Date(m.started_at || m.created_at).toLocaleString()}</div>
              {m.status === "submitted" ? (
                <>
                  <span className="text-sm font-semibold">{m.score_pct}%</span>
                  <Badge className={m.passed ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-rose-500/20 text-rose-300 border-rose-500/40"}>
                    {m.passed ? "PASS" : "FAIL"}
                  </Badge>
                </>
              ) : <Badge variant="outline" className="text-[10px]">{m.status === "expired" ? "expired" : "in progress"}</Badge>}
            </button>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function BuildingScreen() {
  return (
    <Card data-testid="mock-building">
      <CardContent className="pt-10 pb-10 text-center space-y-3">
        <Loader2 className="w-8 h-8 mx-auto animate-spin text-primary" />
        <div className="font-semibold">Building your 25-question mock…</div>
        <div className="text-xs text-muted-foreground">Writing blueprint-aligned scenarios in parallel — usually 20–40 seconds. Timer starts once it's ready.</div>
      </CardContent>
    </Card>
  );
}

/* ------------------------------- Runner ------------------------------- */
function MockRunner({ mock, onSubmitted }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flags, setFlags] = useState({});
  const [remaining, setRemaining] = useState(() => Math.floor((new Date(mock.expires_at) - Date.now()) / 1000));
  const [submitting, setSubmitting] = useState(false);
  const submittedRef = useRef(false);
  const answersRef = useRef(answers);
  answersRef.current = answers;

  const submit = async (auto = false) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/certprep/mock/${mock.mock_id}/submit`, { answers: answersRef.current }, { withCredentials: true });
      if (auto) toast.warning("Time's up — mock auto-submitted");
      onSubmitted(data);
    } catch (e) {
      submittedRef.current = false;
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally { setSubmitting(false); }
  };

  useEffect(() => {
    const t = setInterval(() => {
      const r = Math.floor((new Date(mock.expires_at) - Date.now()) / 1000);
      setRemaining(r);
      if (r <= 0) { clearInterval(t); submit(true); }
    }, 1000);
    return () => clearInterval(t);
  }, [mock.expires_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const q = mock.questions[idx];
  const total = mock.questions.length;
  const answered = Object.keys(answers).length;
  const low = remaining <= 120;

  return (
    <div className="space-y-4" data-testid="mock-runner">
      <Card className={low ? "border-rose-500/60" : ""}>
        <CardContent className="pt-4 pb-4 flex items-center gap-3 flex-wrap">
          <Badge className={EXAM_LABEL[mock.exam].color}>{EXAM_LABEL[mock.exam].name} mock</Badge>
          <div className="text-sm text-muted-foreground">Q {idx + 1}/{total} · {answered} answered</div>
          <div className={`ml-auto font-mono text-xl font-bold tabular-nums ${low ? "text-rose-400 animate-pulse" : ""}`} data-testid="mock-timer">
            <Timer className="w-4 h-4 inline mr-1 -mt-1" />{fmt(remaining)}
          </div>
        </CardContent>
        <Progress value={(answered / total) * 100} className="h-1 rounded-none" />
      </Card>

      <Card data-testid="mock-question">
        <CardHeader>
          <div className="flex items-center gap-2 flex-wrap">
            {mock.exam === "mixed" && <Badge className={`text-[10px] ${EXAM_LABEL[q.exam].color}`}>{EXAM_LABEL[q.exam].name}</Badge>}
            <Button
              size="sm" variant={flags[q.question_id] ? "default" : "ghost"}
              className="ml-auto h-7 text-xs"
              onClick={() => setFlags(f => ({ ...f, [q.question_id]: !f[q.question_id] }))}
              data-testid="mock-flag-btn"
            >
              <Flag className="w-3 h-3 mr-1" /> {flags[q.question_id] ? "Flagged" : "Flag"}
            </Button>
          </div>
          <CardTitle className="text-base md:text-lg leading-relaxed mt-2">{q.stem}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {["A", "B", "C", "D"].map((l) => {
            const sel = answers[q.question_id] === l;
            return (
              <button
                key={l}
                onClick={() => setAnswers(a => ({ ...a, [q.question_id]: l }))}
                data-testid={`mock-option-${l}`}
                className={`w-full text-left border rounded-lg p-3 transition-colors ${sel ? "border-primary bg-primary/10" : "border-zinc-700 hover:border-primary/60 hover:bg-primary/5"}`}
              >
                <span className="font-bold text-primary mr-2">{l}.</span>{q.options[l]}
              </button>
            );
          })}
        </CardContent>
      </Card>

      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="outline" size="sm" disabled={idx === 0} onClick={() => setIdx(i => i - 1)} data-testid="mock-prev-btn">
          <ChevronLeft className="w-4 h-4" /> Prev
        </Button>
        <Button variant="outline" size="sm" disabled={idx === total - 1} onClick={() => setIdx(i => i + 1)} data-testid="mock-next-btn">
          Next <ChevronRight className="w-4 h-4" />
        </Button>
        <Button
          size="sm" className="ml-auto" disabled={submitting}
          onClick={() => {
            if (answered < total && !window.confirm(`${total - answered} unanswered — submit anyway?`)) return;
            submit(false);
          }}
          data-testid="mock-submit-btn"
        >
          {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />} Submit mock
        </Button>
      </div>

      <div className="grid grid-cols-9 sm:grid-cols-[repeat(13,minmax(0,1fr))] gap-1.5" data-testid="mock-palette">
        {mock.questions.map((qq, i) => {
          const a = answers[qq.question_id];
          const cls = i === idx ? "ring-2 ring-primary" : "";
          const fill = a ? "bg-primary/30 border-primary/50" : "border-zinc-700";
          return (
            <button
              key={qq.question_id}
              onClick={() => setIdx(i)}
              className={`relative h-8 rounded border text-xs ${fill} ${cls}`}
              data-testid={`mock-palette-${i + 1}`}
            >
              {i + 1}
              {flags[qq.question_id] && <Flag className="w-2.5 h-2.5 absolute top-0.5 right-0.5 text-amber-400" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------- Results ------------------------------- */
function MockResults({ mock, onDone }) {
  const [open, setOpen] = useState(null);
  const passed = mock.passed;
  const wrongFirst = useMemo(
    () => [...mock.questions].sort((a, b) => Number(a.is_correct) - Number(b.is_correct)),
    [mock.questions],
  );

  return (
    <div className="space-y-4" data-testid="mock-results">
      <Card className={passed ? "border-emerald-500/60 shadow-lg shadow-emerald-500/10" : "border-rose-500/60 shadow-lg shadow-rose-500/10"}>
        <CardContent className="pt-8 pb-8 text-center space-y-2">
          {passed ? <Trophy className="w-10 h-10 mx-auto text-emerald-400" /> : <AlertTriangle className="w-10 h-10 mx-auto text-rose-400" />}
          <div className={`text-4xl sm:text-5xl font-black tracking-tight ${passed ? "text-emerald-300" : "text-rose-300"}`} data-testid="mock-verdict">
            {passed ? "PASS" : "FAIL"}
          </div>
          <div className="text-2xl font-bold" data-testid="mock-score">{mock.score_pct}%</div>
          <div className="text-xs text-muted-foreground">
            {mock.correct}/{mock.question_count} correct · pass mark {mock.pass_pct}% · {fmt(mock.elapsed_sec)} used of {fmt(mock.time_limit_sec)}
            {mock.timed_out && " · timed out"}
          </div>
          <div className="flex justify-center gap-2 flex-wrap pt-2">
            {Object.entries(mock.by_exam || {}).map(([ex, s]) => (
              <Badge key={ex} variant="outline" className={EXAM_LABEL[ex]?.color}>{EXAM_LABEL[ex]?.name} {s.correct}/{s.total}</Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {mock.weak_topics?.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Remediate first</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {mock.weak_topics.map((t) => (
              <div key={t.topic} className="flex items-center gap-3 text-sm">
                <span className="flex-1 truncate">{t.topic}</span>
                <span className="text-muted-foreground text-xs">{t.correct}/{t.total}</span>
                <Progress value={t.accuracy} className="w-24 h-1.5" />
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Review — misses first</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {wrongFirst.map((q, i) => (
            <div key={q.question_id} className="border border-zinc-800 rounded-lg" data-testid={`mock-review-${i}`}>
              <button className="w-full flex items-start gap-2 p-3 text-left" onClick={() => setOpen(open === q.question_id ? null : q.question_id)}>
                {q.is_correct ? <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> : <XCircle className="w-4 h-4 text-rose-400 mt-0.5 shrink-0" />}
                <span className="text-sm flex-1 line-clamp-2">{q.stem}</span>
                <Badge variant="outline" className="text-[10px] shrink-0">{q.topic}</Badge>
              </button>
              {open === q.question_id && (
                <div className="px-3 pb-3 space-y-2 text-sm">
                  {["A", "B", "C", "D"].map((l) => (
                    <div key={l} className={`p-2 rounded border ${l === q.correct ? "border-emerald-500 bg-emerald-500/10" : l === q.chosen ? "border-rose-500 bg-rose-500/10" : "border-zinc-800 opacity-70"}`}>
                      <b>{l}.</b> {q.options[l]}
                      {l !== q.correct && q.distractor_notes?.[l] && <div className="text-xs text-muted-foreground mt-1">{q.distractor_notes[l]}</div>}
                    </div>
                  ))}
                  <div className="p-3 rounded bg-primary/5 border border-primary/30 text-sm">
                    <b>Why {q.correct}:</b> {q.rationale}
                    {!q.chosen && <div className="text-xs text-muted-foreground mt-1">You left this blank.</div>}
                  </div>
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      <Button onClick={onDone} variant="outline" data-testid="mock-done-btn"><RotateCcw className="w-4 h-4 mr-2" /> Back to mock setup</Button>
    </div>
  );
}
